import { cn } from "@/lib/utils";
import { maskitoDateOptionsGenerator } from "@maskito/kit";
import { useMaskito } from "@maskito/react";
import { parse } from "date-fns";
import { format } from "date-fns-tz";
import { de } from "date-fns/locale";
import { CalendarIcon, X } from "lucide-react";
import React, { useEffect } from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// TODO: we need to localize this date picker option generator.
const mask = maskitoDateOptionsGenerator({
  mode: "dd/mm/yyyy",
  separator: ".",
});

type Props = {
  value?: Date;
  onChange: (value: Date | null) => void;
  disabled?: boolean | string | React.ReactNode;
  onBlur?: any;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  readOnly?: boolean | string | React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  align?: "start" | "center" | "end";
  avoidCollisions?: boolean;
  clearButton?: boolean;
  onClear?: () => void;
};

const generateStringDate = (date: Date | undefined | null) => {
  if (!date || date.toString() === "Invalid Date") {
    return "";
  }
  return format(date, "dd.MM.yyyy");
};

export const DateTimePicker = ({
  value: _value,
  className,
  onChange,
  disabled,
  onBlur,
  onFocus,
  readOnly,
  side,
  sideOffset,
  align,
  avoidCollisions,
  clearButton = false,
  onClear,
}: Props) => {
  const [inputValue, setInputValue] = React.useState<string>(
    generateStringDate(_value),
  );
  const [value, setValue] = React.useState<Date | undefined>(_value);
  const maskedInputRef = useMaskito({ options: mask });

  useEffect(() => {
    setValue(_value);
    setInputValue(generateStringDate(_value));
  }, [_value]);

  const handleOnChange = (date: Date | undefined) => {
    if (!date) {
      onChange(null);
    } else if (date.toString() === "Invalid Date") {
      onChange(value ?? null);
    } else {
      onChange(date);
    }
  };

  const handleClear = () => {
    setValue(undefined);
    setInputValue("");
    onChange(null);

    onClear?.();
  };

  const message = React.useMemo(() => {
    if (disabled) {
      return disabled;
    }
    if (readOnly) {
      return readOnly;
    }
    return "";
  }, [disabled, readOnly]);

  const datePickerContent = (
    <Popover
      modal
      onOpenChange={(open) => {
        if (!open) {
          onBlur?.();
        }
      }}
    >
      <div
        className={cn(
          "relative rounded-md border border-input bg-background focus:outline-none focus-visible:outline-muted-foreground disabled:cursor-not-allowed read-only:cursor-not-allowed disabled:border-opacity-50",
          Boolean(readOnly) && "cursor-not-allowed disabled:opacity-100",
          className,
        )}
      >
        <Input
          type="string"
          value={inputValue}
          ref={maskedInputRef}
          className={cn("border-0 bg-transparent")}
          onFocus={onFocus}
          placeholder="TT.MM.JJJJ"
          onBlur={onBlur}
          readOnly={Boolean(readOnly)}
          disabled={Boolean(disabled) || Boolean(readOnly)}
          onInput={(e: any) => {
            const value = e.target.value;
            setInputValue(value);
            const parsedDate = parse(value, "dd.MM.yyyy", new Date());
            setValue(parsedDate);
            if (value.length === 0) {
              handleOnChange(undefined);
            }
            if (value.length === 10) {
              handleOnChange(parsedDate);
            }
          }}
        />
        <PopoverTrigger asChild>
          <Button
            variant={"outline"}
            tabIndex={-1}
            disabled={Boolean(disabled) || Boolean(readOnly)}
            className={cn(
              "font-normal absolute right-0 translate-y-[-50%] top-[50%] rounded-l-none border-0 bg-transparent h-9 mr-0.5 text-muted-foreground p-2",
            )}
          >
            <CalendarIcon className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
      </div>
      <PopoverContent
        className="w-auto p-0 z-[2000]"
        side={side}
        sideOffset={sideOffset}
        align={align}
        avoidCollisions={avoidCollisions}
      >
        <Calendar
          mode="single"
          selected={value}
          onSelect={(selectedDate) => {
            if (!selectedDate) return;
            setValue(selectedDate);
            handleOnChange(selectedDate);
            const stringDate = format(selectedDate, "dd.MM.yyyy");
            setInputValue(stringDate);
          }}
          defaultMonth={value}
          locale={de}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );

  const content = clearButton ? (
    <div className="flex items-end gap-2">
      <div className="flex-1">{datePickerContent}</div>
      {value && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-10 w-10 p-0 text-muted-foreground hover:text-foreground"
          onClick={handleClear}
          disabled={Boolean(disabled) || Boolean(readOnly)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  ) : (
    datePickerContent
  );

  if (
    typeof disabled === "string" ||
    typeof readOnly === "string" ||
    typeof readOnly === "object" ||
    typeof disabled === "object"
  ) {
    return (
      <HoverCard openDelay={1000} closeDelay={0}>
        <HoverCardTrigger>{content}</HoverCardTrigger>
        {Boolean(disabled || readOnly) && (
          <HoverCardContent
            align="start"
            sideOffset={-5}
            className="w-fit max-w-[500px] p-3"
          >
            {typeof message === "string" ? (
              <p className="text-sm">{message}</p>
            ) : (
              message
            )}
          </HoverCardContent>
        )}
      </HoverCard>
    );
  }

  return content;
};
